import clsx from 'clsx';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { toMultipassLegId } from '@/api/option/utils.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { GroupSummaryField } from '@/App/tiles/Option/strategy/GroupedPricingSummary/GroupSummaryField.tsx';
import { getMarkupCurrencyIndex } from '@/App/tiles/Option/strategy/GroupedPricingSummary/utils.ts';
import { getFieldRow, getFieldSpan, isHedgeField } from '@/App/tiles/Option/strategy/strategyLayout.ts';
import type { StrategyFields, StrategyOrientation } from '@/App/tiles/Option/strategy/types.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { getQuote, selectGroupedByExpiry, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { OptionLegProperties } from '@/store/option/state.ts';

type GroupedPricingSummaryProps = {
  legs: OptionLegProperties[];
  fields: StrategyFields;
  groupColumn: number;
  isOpen: boolean;
  mode: StrategyOrientation;
  canGroupHedges: boolean;
};

/**
 * Grouped pricing cases (no summary when expiry grouping is disabled):
 * - Horizontal, open or closed: totals stay in the group header; legs are shown only when open.
 * - Vertical, open: premiums/Greeks stay per leg; no pricing summary is rendered.
 * - Vertical, closed: totals occupy one column; unshared hedge fields are omitted.
 * - No quote: pricing cells stay blank. SharedHedgeFields owns all shared editors and wrappers.
 */
export function GroupedPricingSummary(props: GroupedPricingSummaryProps) {
  const { fields, legs, groupColumn, isOpen, mode, canGroupHedges } = props;
  const {
    currentUser: { userType },
  } = useUserContext();
  const {
    quote,
    productName,
    premiumType,
    markupCurrency,
    currencyPair,
    currency1,
    currency2,
    isExpired,
    uiState,
    groupedByExpiry,
  } = useOptionSelector((option) => ({
    quote: getQuote(option),
    productName: option.type,
    premiumType: option.properties.groupedPremiumType ?? option.properties.premiumType,
    markupCurrency: option.properties.markupCurrency,
    ...selectOptionCurrency(option),
    isExpired: option.properties.rfs.status === 'EXPIRED',
    uiState: option.properties.uiState,
    groupedByExpiry: selectGroupedByExpiry(option),
  }));
  const currencyPairPrecision = useAppSelector((state) => selectCurrencyPairPrecision(state, currencyPair));

  if (!groupedByExpiry || uiState.strategyPanelMode !== mode || legs.length === 0 || (mode === 'vertical' && isOpen)) {
    return undefined;
  }

  const shared = {
    legs: legs.map((leg) => ({ leg, data: quote?.legsData[toMultipassLegId(leg.legId, productName)] })),
    premiumType,
    mode,
    markupCurrencyIndex: getMarkupCurrencyIndex(markupCurrency, currency1, currency2),
    currency: premiumType.premiumCurrency === 1 ? currency1 : currency2,
    currency1,
    currencyPairPrecision,
    isExpired,
    showPricing: quote !== undefined,
    isInternalSales: userType === 'Internal Sales',
  };

  if (mode === 'horizontal') {
    return fields
      .flat()
      .map((field, index) =>
        canGroupHedges && isHedgeField(field) ? undefined : (
          <GroupSummaryField key={field} {...shared} field={field} style={{ gridColumn: index + 2, gridRow: 1 }} />
        ),
      );
  }

  return fields.map((fieldOrGroup, index) => {
    const row = getFieldRow(fields, index);
    const style = { gridColumn: groupColumn, gridRow: row };

    if (!Array.isArray(fieldOrGroup)) {
      return canGroupHedges && isHedgeField(fieldOrGroup) ? undefined : (
        <GroupSummaryField key={fieldOrGroup} {...shared} field={fieldOrGroup} style={style} />
      );
    }

    // SharedHedgeFields owns hedge wrappers; unshared hedges have no collapsed aggregate.
    if (fieldOrGroup.some(isHedgeField)) {
      return undefined;
    }

    return (
      <div
        key={fieldOrGroup.join('-')}
        className={clsx('fieldGroup bg-lvl2', { 'pricing-fields': fieldOrGroup.includes('premium') })}
        style={{
          gridColumn: `${groupColumn} / span 1`,
          gridRow: `${row} / span ${getFieldSpan(fieldOrGroup)}`,
        }}
      >
        {fieldOrGroup.map((field) => (
          <GroupSummaryField key={field} {...shared} field={field} />
        ))}
      </div>
    );
  });
}
